// hooks/features/apiKeys/useApiKeyModule.ts
import { useState, useEffect } from 'react';
import useApiKeys from './useApiKeys';
import { alertSuccess, alertError } from '../../../helpers/alertError';
import { ApiKey, CreateApiKeyRequest } from '../../../types/features/ApiKey';

export const useApiKeyModule = (moduleName: string) => {
  const {
    apiKeys,
    loading,
    getApiKeys,
    createApiKey,
    regenerateApiKey,
    deleteApiKey,
    toggleApiKey,
  } = useApiKeys();

  const [copied, setCopied] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<any>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    module: moduleName as ApiKey["module"],
    permissions: ['create', 'read', 'update'] as string[],
    expires_at: '',
  });

  const moduleKeys = apiKeys.filter(key => key.module === moduleName);
  const moduleKey = moduleKeys.find(key => key.module === moduleName);

  useEffect(() => {
    getApiKeys();
  }, []);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCreateKey = async () => {
    if (!formData.name.trim()) {
      alertError('Le nom est requis');
      return;
    }

    try {
      const result = await createApiKey({
        name: formData.name,
        module: formData.module as CreateApiKeyRequest["module"],
        permissions: formData.permissions,
        expires_at: formData.expires_at || undefined,
      });

      if (result) {
        alertSuccess('Clé API créée avec succès');
        setNewKeySecret(result.key);
        setIsModalOpen(false);
        setFormData({ name: '', module: moduleName, permissions: ['create', 'read', 'update'], expires_at: '' });
        await getApiKeys();
      }
    } catch (error: any) {
      alertError(error.response?.data?.message || 'Erreur lors de la création');
    }
  };

  const handleRegenerate = async (id: string) => {
    if (!confirm('Voulez-vous vraiment régénérer cette clé ?')) return;
    setIsRegenerating(true);
    try {
      const newKey = await regenerateApiKey(id);
      if (newKey) {
        alertSuccess('Clé régénérée avec succès');
        setNewKeySecret(newKey);
        await getApiKeys();
      }
    } catch (error: any) {
      alertError(error.response?.data?.message || 'Erreur lors de la régénération');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;
    try {
      await deleteApiKey(keyToDelete.id);
      alertSuccess('Clé supprimée avec succès');
      setKeyToDelete(null);
      await getApiKeys();
    } catch (error: any) {
      alertError(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleKey = async (id: string) => {
    try {
      await toggleApiKey(id);
      await getApiKeys();
    } catch (error: any) {
      alertError(error.response?.data?.message || 'Erreur lors du changement de statut');
    }
  };

  return {
    // Données
    apiKeys: moduleKeys,
    moduleKey,
    loading,
    copied,
    isModalOpen,
    keyToDelete,
    isRegenerating,
    newKeySecret,
    formData,

    // Setters
    setCopied,
    setIsModalOpen,
    setKeyToDelete,
    setNewKeySecret,
    setFormData,

    // Actions
    copyToClipboard,
    handleCreateKey,
    handleRegenerate,
    handleDeleteKey,
    handleToggleKey,
    getApiKeys,
  };
};